import React, {
  useEffect, useMemo, useState, Key,
} from 'react';
import classNames from 'classnames';
import { Radio, Table as AntdTable, TableProps as AntdTableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { Button, ButtonType } from 'components/Controls/Button';
import { SettingsIconButton } from 'components/Icons/Settings';
import { Checkbox } from 'components/Controls/Checkbox';
import { Tooltip, TooltipPlacement } from 'components/Tooltip';

import { filterConfig } from 'components/Table/utils';
import { ExpandIcon } from './ExpandIcon';

import { ColumnType } from './types';
import cn from './styles.module.scss';

export type { ColumnType } from './types';

export enum OrderMap {
  ascend = 'ASC',
  descend = 'DESC',
}

function isReactElement(node: React.ReactNode): node is React.ReactElement {
  return React.isValidElement(node);
}

export type TableProps<RecordType> = AntdTableProps<RecordType> & {
  columns: ColumnType<RecordType>[],
  creatable?: boolean,
  onRowSave?: () => any,
  onSortingChange?: (newSorting: { [key: string]: keyof typeof OrderMap }) => any,
  total?: string | React.ReactNode,
  showTabBar?: boolean,
  onSettingsClick?: React.MouseEventHandler,
  pagination?: boolean | any,
  tableClassName?: string,
  settingsButtonTestId?: string
  AddRowForm?: React.ComponentType<any>
};

export function Table<RecordType extends { [key: string]: any } = any>(
  {
    columns: columnsProp,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tableLayout,
    dataSource,
    total,
    creatable,
    onRowSave,
    onSortingChange,
    rowSelection,
    rowKey = 'id',
    showHeader = true,
    showTabBar = true,
    pagination = true,
    bordered = true,
    expandable,
    onSettingsClick,
    tableClassName,
    className,
    settingsButtonTestId,
    AddRowForm,
    ...restProps
  }: TableProps<RecordType>,
) {
  const [formOpen, setFormOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<readonly Key[]>([]);

  const pageDataLength = dataSource?.length || 0;

  const paginationConfig = useMemo(() => {
    if (typeof pagination === 'boolean') {
      return pagination ? { className: cn.pagination } : false;
    }

    return {
      total: pageDataLength && (pageDataLength % pagination.pageSize === 0)
        ? pagination.current * pagination.pageSize + 1
        : (pagination.current - 1) * pagination.pageSize + Math.max(pageDataLength, 1),
      showSizeChanger: true,
      showLessItems: true,
      pageSizeOptions: [10, 25, 50],
      className: cn.pagination,
      ...pagination,
      onChange: pagination.onChange ? (page: number, pageSize: number) => {
        pagination.onChange(
          pageSize === pagination.pageSize ? page : 1,
          pageSize,
        );
      } : undefined,
    };
  }, [pagination, pageDataLength]);

  const columns = useMemo(() => filterConfig(columnsProp)
    .map((column) => {
      if (column.children) {
        return {
          ...column,
          className: classNames(column.className, cn.parentCell),
          children: column.children.map((child: ColumnType<RecordType>, index: number) => ({
            ...child,
            className: classNames(child.className, cn.group, {
              [cn.borderLeft]: index === 0,
              [cn.borderRight]: index === (column.children || []).length - 1,
            }),
          })),
        };
      }
      return {
        ...column,
        className: classNames(column.className, cn.cell),
      };
    }), [columnsProp, creatable]);

  const handleAddRow = () => {
    setFormOpen(true);
  };

  const handleChange: any = (paginationDirection: any, filters: any, sorter: any, extra: any) => {
    if (onSortingChange && extra.action === 'sort') {
      const sorting = [];
      if (!Array.isArray(sorter)) {
        sorting.push(sorter);
      } else {
        sorting.push(...sorter);
      }

      onSortingChange(
        sorting.reduce((result, { columnKey, order } : { order: keyof typeof OrderMap, columnKey: string }) => ({
          ...result,
          [columnKey]: OrderMap[order],
        }), {}),
      );
    }
  };

  const onExpandedRowsChange = (keys: readonly Key[]) => {
    setExpandedRows(keys);
  };

  useEffect(() => {
    if (expandable && expandable.expandedRowKeys) {
      setExpandedRows(expandable.expandedRowKeys);
    }
  }, [expandable]);

  return (
    <div className={classNames(
      cn.tableContainer,
      { [cn.bordered]: bordered },
      { [cn.withoutTabBar]: !showTabBar },
      className,
    )}
    >
      {showTabBar && (
        <div className={classNames(
          cn.tableBar,
          { [cn.tableBarEmpty]: !creatable && !AddRowForm && !total },
        )}
        >
          {creatable && !formOpen && (
            <div className={cn.addButton}>
              <Button disabled={formOpen} onClick={handleAddRow} className={cn.button}>
                <PlusOutlined className={cn.icon} />
              </Button>
            </div>
          )}
          {AddRowForm && formOpen && (
            <AddRowForm onSubmit={onRowSave} onCancel={() => setFormOpen(false)} />
          )}
          {total && <div className={cn.total}>{total}</div>}
        </div>
      )}
      <AntdTable
        className={tableClassName}
        columns={columns}
        dataSource={dataSource}
        rowKey={rowKey}
        showHeader={showHeader}
        pagination={paginationConfig}
        onChange={handleChange}
        expandable={expandable && {
          expandIcon: (props) => {
            if (props.expandable) {
              return <ExpandIcon {...props} />;
            }

            return null;
          },
          onExpandedRowsChange,
          ...expandable,
          expandedRowRender: (record, index, indent, expanded) => {
            if (expanded && expandable.expandedRowRender) {
              return expandable.expandedRowRender(record, index, indent, expanded);
            }

            return null;
          },
        }}
        rowSelection={
          rowSelection ? {
            ...rowSelection,
            renderCell: function renderSelection(checked, record, index, node) {
              if (rowSelection.type === 'radio') {
                return <Radio {...(isReactElement(node) ? node.props : {})} checked={checked} />;
              }

              return (
                <Checkbox {...(isReactElement(node) ? node.props : {})} checked={checked} />
              );
            },
          } : undefined
        }
        rowClassName={(record) => (expandedRows.includes(record.id) ? cn.expandedRow : '')}
        {...restProps}
        title={onSettingsClick ? () => (
          <div className={cn.settingButton}>
            <Tooltip placement={TooltipPlacement.Left} title="Edit Display Field">
              <SettingsIconButton
                data-testid={settingsButtonTestId}
                onClick={onSettingsClick}
                iconSize={24}
                type={ButtonType.Link}
              />
            </Tooltip>
          </div>
        ) : undefined}
      />
    </div>
  );
}
